import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'

export interface SendInviteInput {
  appearance_id: string
  guest_id: string
  schedule_id?: string // Optional - for Calendly booking link
  meeting_link?: string // Optional - for manual meeting link
  custom_message?: string
}

export interface SendReminderInput {
  appearance_id: string
  guest_id: string
}

/**
 * Hook to send an invitation to a guest
 * Optionally creates a Calendly booking link before sending
 */
export function useSendInvite() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SendInviteInput) => {
      const { appearance_id, guest_id, schedule_id, meeting_link, custom_message } = input

      // Ensure we have a fresh session and get access token
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData?.session) {
        const { error: refreshError } = await supabase.auth.refreshSession()
        if (refreshError) {
          throw new Error('Session expired. Please refresh the page and log in again.')
        }
      }

      const accessToken = sessionData?.session?.access_token
      if (!accessToken) {
        throw new Error('No access token available. Please log in again.')
      }

      let bookingUrl: string | undefined

      // 1. If using Calendly, create booking link first
      if (schedule_id) {
        const bookingResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendly-create-booking-link`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              appearance_id,
              schedule_id,
            }),
          }
        )

        const bookingData = await bookingResponse.json()
        if (!bookingResponse.ok) {
          throw new Error(bookingData.error || 'Failed to create booking link')
        }
        bookingUrl = bookingData.booking_url
      }

      // 2. Send invitation email
      const variables: Record<string, string> = {}
      if (bookingUrl) variables.booking_url = bookingUrl
      if (meeting_link) variables.meeting_link = meeting_link
      if (custom_message) variables.custom_message = custom_message

      const emailResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            guest_id,
            appearance_id,
            template_type: 'invitation',
            variables,
          }),
        }
      )

      const emailData = await emailResponse.json()
      if (!emailResponse.ok) {
        throw new Error(emailData.error || 'Failed to send invitation email')
      }

      // 3. Update appearance stage to 'invited' if not already beyond that
      const { data: appearance } = await supabase
        .from('appearances')
        .select('stage')
        .eq('id', appearance_id)
        .single()

      // Only update if still at 'invited' stage (don't regress)
      const currentStage = (appearance as { stage: string } | null)?.stage
      if (currentStage === 'invited') {
        await supabase
          .from('appearances')
          .update({ stage: 'invited' } as never)
          .eq('id', appearance_id)
      }

      return {
        success: true,
        booking_url: bookingUrl,
        email: emailData,
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
      queryClient.invalidateQueries({ queryKey: ['communications'] })
    },
  })
}

/**
 * Hook to send a reminder email to a guest
 */
export function useSendReminder() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SendReminderInput) => {
      const { appearance_id, guest_id } = input

      console.log('Sending reminder:', { appearance_id, guest_id })

      // Get fresh session and pass token explicitly
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      console.log('Session check:', { hasSession: !!sessionData?.session, error: sessionError?.message })

      if (!sessionData?.session) {
        const { error: refreshError } = await supabase.auth.refreshSession()
        if (refreshError) {
          console.error('Session refresh failed:', refreshError)
          throw new Error('Session expired. Please refresh the page and log in again.')
        }
      }

      // Get the access token to pass explicitly in headers
      const accessToken = sessionData?.session?.access_token
      console.log('Access token present:', !!accessToken, accessToken?.substring(0, 20))

      if (!accessToken) {
        throw new Error('No access token available. Please log in again.')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            guest_id,
            appearance_id,
            template_type: 'reminder',
          }),
        }
      )

      const data = await response.json()
      console.log('Send reminder response:', { data, ok: response.ok, status: response.status })

      if (!response.ok) {
        console.error('Send reminder error:', data)
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page and try again.')
        }
        throw new Error(data.error || 'Failed to send reminder')
      }

      // Check if the response contains an error
      if (data?.error) {
        console.error('Send reminder data error:', data.error)
        throw new Error(data.error)
      }

      if (!data?.success) {
        console.error('Send reminder unexpected response:', data)
        throw new Error('Email may not have been sent. Please check and try again.')
      }

      console.log('Reminder sent successfully:', data)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications'] })
    },
  })
}

/**
 * Hook to send recording details email to a guest
 */
export function useSendRecordingDetails() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SendReminderInput & { custom_message?: string }) => {
      const { appearance_id, guest_id, custom_message } = input

      // Get access token
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token
      if (!accessToken) {
        throw new Error('No access token available. Please log in again.')
      }

      const variables: Record<string, string> = {}
      if (custom_message) variables.custom_message = custom_message

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            guest_id,
            appearance_id,
            template_type: 'recording_details',
            variables,
          }),
        }
      )

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send recording details')
      }
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications'] })
    },
  })
}
// Build timestamp: 1770057477
